import { useEffect, useMemo, useState, Fragment } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { DashboardDateRangePicker } from '@/components/dashboard/DashboardDateRangePicker';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/utils/cn';
import {
  adminApi,
  type AdminReportCoverageHole,
  type ReportCoverageEnqueueBody,
  type ReportCoverageParams,
} from '@/api/adminApi';
import {
  CoverageListFilters,
  CoveragePresetId,
  CoverageRollingHourCards,
  CoverageStatCards,
  STAGE_BADGE,
  STAGE_LABEL,
  accountMatchesCoverageFilters,
  adsCoveragePickerBounds,
  clampRange,
  coverageRequestParams,
  defaultAdsLookbackRange,
  defaultAdsThisMonthRange,
  enqueueToastMessage,
  holeMatchesCoverageFilters,
  isHealableHole,
  queryLabel,
  rowCountsForQuery,
  windowLabel,
} from '@/pages/admin/reportCoverageShared';

const COVERAGE_PRESETS = [
  { id: 'lookback', label: 'Last 3 months' },
  { id: 'freshness', label: 'This month' },
];

function useCoverageEnqueue(profileId: string, coverageParams: ReportCoverageParams) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (body: ReportCoverageEnqueueBody) =>
      adminApi.enqueueReportCoverageHoles(profileId, { ...coverageParams, ...body }),
    onSuccess: (res) => {
      const result = res.data;
      showToast(enqueueToastMessage(result), result.enqueued > 0 ? 'success' : 'error');
      void queryClient.invalidateQueries({ queryKey: ['admin-report-coverage'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-report-coverage-holes', profileId] });
    },
    onError: (err) => {
      showToast((err as Error).message, 'error');
    },
  });
}

function CoverageHoles({
  profileId,
  coverageParams,
  queryLabels,
  stageFilter,
  queryFilter,
}: {
  profileId: string;
  coverageParams: ReportCoverageParams;
  queryLabels: Record<string, string>;
  stageFilter: string;
  queryFilter: string;
}) {
  const enqueueMutation = useCoverageEnqueue(profileId, coverageParams);
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-report-coverage-holes', profileId, coverageParams],
    queryFn: () => adminApi.getReportCoverageHoles(profileId, coverageParams),
  });
  const holes = data?.data?.holes ?? [];
  const filtered = useMemo(
    () => holes.filter((hole) => holeMatchesCoverageFilters(hole, stageFilter, queryFilter)),
    [holes, queryFilter, stageFilter]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="w-6 h-6" />
      </div>
    );
  }
  if (error) {
    return <p className="px-4 py-4 text-error">{(error as Error).message}</p>;
  }

  return (
    <div className="px-4 py-4 bg-page/60">
      <p className="text-xs text-foreground-subtle mb-3">
        {filtered.length} of {holes.length} windows
      </p>
      {filtered.length === 0 ? (
        <p className="text-sm text-foreground-muted">No windows match these filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-surface">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-left text-foreground-muted">
                <th className="px-3 py-2 font-medium">Report</th>
                <th className="px-3 py-2 font-medium">Window</th>
                <th className="px-3 py-2 font-medium">Stage</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">S3</th>
                <th className="px-3 py-2 font-medium">DB</th>
                <th className="px-3 py-2 font-medium">Failure</th>
                <th className="px-3 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((hole: AdminReportCoverageHole) => {
                const healable = isHealableHole(hole);
                const pending =
                  enqueueMutation.isPending &&
                  enqueueMutation.variables?.query_id === hole.query_id &&
                  enqueueMutation.variables?.window_start === hole.window_start;
                return (
                  <tr
                    key={`${hole.query_id}:${hole.window_start}:${hole.window_end}`}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-3 py-2">{queryLabel(hole.query_id, queryLabels)}</td>
                    <td className="px-3 py-2 font-mono">{windowLabel(hole.window_start, hole.window_end)}</td>
                    <td className="px-3 py-2">
                      <Badge variant={STAGE_BADGE[hole.stage]}>{STAGE_LABEL[hole.stage]}</Badge>
                    </td>
                    <td className="px-3 py-2 text-foreground-muted">{hole.status || '—'}</td>
                    <td className="px-3 py-2">{hole.has_s3 ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2">{hole.has_db ? 'Yes' : 'No'}</td>
                    <td
                      className="px-3 py-2 text-foreground-muted max-w-xs truncate"
                      title={hole.failure_reason || hole.failure_class || ''}
                    >
                      {hole.failure_reason || hole.failure_class || '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {healable ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={enqueueMutation.isPending}
                          onClick={() =>
                            enqueueMutation.mutate({
                              query_id: hole.query_id,
                              window_start: hole.window_start,
                              window_end: hole.window_end,
                            })
                          }
                        >
                          {pending ? 'Working…' : hole.stage === 'failed' ? 'Retry' : 'Enqueue'}
                        </Button>
                      ) : (
                        <span className="text-foreground-subtle">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HealMissingButton({
  profileId,
  coverageParams,
  healableCount,
}: {
  profileId: string;
  coverageParams: ReportCoverageParams;
  healableCount: number;
}) {
  const enqueueMutation = useCoverageEnqueue(profileId, coverageParams);
  return (
    <Button
      size="sm"
      variant="secondary"
      disabled={healableCount < 1 || enqueueMutation.isPending}
      onClick={() => enqueueMutation.mutate({ stages: ['failed', 'not_scheduled', 'scheduled'] })}
    >
      {enqueueMutation.isPending ? 'Healing…' : 'Heal missing'}
    </Button>
  );
}

export function AmazonAdsCoveragePanel() {
  const defaults = defaultAdsLookbackRange();
  const [presetId, setPresetId] = useState<CoveragePresetId>('lookback');
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [stageFilter, setStageFilter] = useState('all');
  const [queryFilter, setQueryFilter] = useState('all');
  const { fromDate, toDate } = adsCoveragePickerBounds();
  const coverageParams = coverageRequestParams(presetId, startDate, endDate);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-report-coverage', coverageParams],
    queryFn: () => adminApi.getReportCoverage(coverageParams),
  });

  const summary = data?.data;
  const rows = summary?.items ?? [];
  const totals = summary?.totals;
  const filteredRows = useMemo(
    () => rows.filter((row) => accountMatchesCoverageFilters(row, stageFilter, queryFilter)),
    [queryFilter, rows, stageFilter]
  );
  const visibleQueryIds = queryFilter === 'all' ? (summary?.query_ids ?? []) : [queryFilter];

  useEffect(() => {
    if (expandedId && !filteredRows.some((row) => row.profile_id === expandedId)) {
      setExpandedId(null);
    }
  }, [expandedId, filteredRows]);

  useEffect(() => {
    if (presetId === 'custom' || !summary?.date_range) return;
    setStartDate(summary.date_range.startDate);
    setEndDate(summary.date_range.endDate);
  }, [presetId, summary?.date_range]);

  const onPresetSelect = (id: string) => {
    const next = id === 'freshness' ? defaultAdsThisMonthRange() : defaultAdsLookbackRange();
    setPresetId(id === 'freshness' ? 'freshness' : 'lookback');
    setStartDate(next.startDate);
    setEndDate(next.endDate);
    setExpandedId(null);
  };

  const onCustomRange = (range: { startDate: string; endDate: string }) => {
    const clamped = clampRange(range.startDate, range.endDate, { fromDate, toDate });
    setPresetId('custom');
    setStartDate(clamped.startDate);
    setEndDate(clamped.endDate);
    setExpandedId(null);
  };

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-foreground-muted mb-1">
            Five Unified reports (windows × 5): Core Performance, Placement, Search Term,
            Audience, and Product. Core powers campaign, ad group, keyword, match type, and
            targeting facts. Placement stays its own Amazon query because targeting and placement
            cannot be combined. Expected through day before yesterday; yesterday is excluded.
          </p>
          <p className="text-sm text-foreground-subtle">
            {startDate} – {endDate}
            {presetId === 'freshness'
              ? ' · expected 1st through day before yesterday; missing contiguous days are clubbed (max one calendar month)'
              : ' · complete months plus this month through D-2; missing contiguous days are clubbed (max one calendar month)'}
          </p>
        </div>
        <DashboardDateRangePicker
          variant="default"
          showHelp={false}
          startDate={startDate}
          endDate={endDate}
          fromDate={fromDate}
          toDate={toDate}
          presets={COVERAGE_PRESETS}
          activePresetId={presetId === 'custom' ? undefined : presetId}
          onPresetSelect={onPresetSelect}
          onChange={onCustomRange}
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="w-10 h-10" />
        </div>
      ) : error ? (
        <p className="text-error">{(error as Error).message}</p>
      ) : (
        <>
          <CoverageStatCards totals={totals} />
          <CoverageRollingHourCards s3Saved={summary?.s3_saved} failed={summary?.failed} />

          <CoverageListFilters
            stageFilter={stageFilter}
            queryFilter={queryFilter}
            queryIds={summary?.query_ids ?? []}
            queryLabels={summary?.query_labels ?? {}}
            onStageChange={setStageFilter}
            onQueryChange={setQueryFilter}
            resultLabel={`${filteredRows.length} of ${rows.length} accounts`}
          />

          <div className="overflow-x-auto rounded-xl border border-border bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-foreground-muted">
                  <th className="px-4 py-3 font-medium w-8" />
                  <th className="px-4 py-3 font-medium">Account</th>
                  <th className="px-4 py-3 font-medium">By report (missing DB)</th>
                  <th className="px-4 py-3 font-medium text-right">Expected</th>
                  <th className="px-4 py-3 font-medium text-right">In DB</th>
                  <th className="px-4 py-3 font-medium text-right">Not scheduled</th>
                  <th className="px-4 py-3 font-medium text-right">Scheduled</th>
                  <th className="px-4 py-3 font-medium text-right">Failed</th>
                  <th className="px-4 py-3 font-medium text-right">S3 only</th>
                  <th className="px-4 py-3 font-medium text-right">Missing S3</th>
                  <th className="px-4 py-3 font-medium text-right">Missing DB</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-8 text-center text-foreground-muted">
                      No accounts found.
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-8 text-center text-foreground-muted">
                      No accounts match these filters.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => {
                    const open = expandedId === row.profile_id;
                    const counts = rowCountsForQuery(row, queryFilter);
                    const healableCount = counts.not_scheduled + counts.failed + counts.scheduled;
                    return (
                      <Fragment key={row.profile_id}>
                        <tr className="border-b border-border last:border-0">
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              aria-expanded={open}
                              aria-label={`Show report windows for ${row.account_name || row.profile_id}`}
                              onClick={() => setExpandedId(open ? null : row.profile_id)}
                            >
                              <ChevronDown
                                className={cn('w-4 h-4 text-foreground-muted transition-transform', open && 'rotate-180')}
                                aria-hidden
                              />
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/accounts/${encodeURIComponent(row.profile_id)}/campaigns/workspace`}
                              className="text-brand hover:underline font-medium"
                            >
                              {row.account_name || row.profile_id}
                            </Link>
                            <div className="text-xs font-mono text-foreground-subtle mt-0.5">{row.profile_id}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs tabular-nums">
                              {visibleQueryIds.map((queryId) => {
                                const missing = row.by_query?.[queryId]?.missing_db ?? 0;
                                const expectedForQuery = row.by_query?.[queryId]?.expected ?? 0;
                                const okForQuery = row.by_query?.[queryId]?.ok ?? 0;
                                return (
                                  <span
                                    key={queryId}
                                    className={cn(
                                      'whitespace-nowrap',
                                      missing > 0 ? 'text-foreground font-medium' : 'text-foreground-subtle'
                                    )}
                                    title={`${queryLabel(queryId, summary?.query_labels)}: ${okForQuery}/${expectedForQuery} in DB`}
                                  >
                                    {queryLabel(queryId, summary?.query_labels)} {missing}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">{counts.expected}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{counts.ok}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{counts.not_scheduled}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{counts.scheduled}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{counts.failed}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{counts.in_s3_not_db}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{counts.missing_s3}</td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium">{counts.missing_db}</td>
                          <td className="px-4 py-3 text-right">
                            <HealMissingButton
                              profileId={row.profile_id}
                              coverageParams={coverageParams}
                              healableCount={healableCount}
                            />
                          </td>
                        </tr>
                        {open ? (
                          <tr className="border-b border-border">
                            <td colSpan={12} className="p-0">
                              <CoverageHoles
                                profileId={row.profile_id}
                                coverageParams={coverageParams}
                                queryLabels={summary?.query_labels ?? {}}
                                stageFilter={stageFilter}
                                queryFilter={queryFilter}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
